import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { businessAPI } from '../../services/apiClient';
import BButton from './ui/BButton';
import BLoading from './ui/BLoading';
import styles from './BusinessAIAnalyticsOverview.module.css';

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const BusinessAIAnalyticsOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await businessAPI.listBusinessAIAnalytics();
      setBusinesses(response?.data?.businesses || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load AI analytics.');
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return <BLoading variant="page" label="Loading AI analytics..." />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={`${styles.title} page-title`}>AI Analytics</h1>
          <p className={styles.subtitle}>
            AI surveys created, credits used, and total AI cost by business.
          </p>
        </div>
        <BButton variant="secondary" size="sm" onClick={loadAnalytics}>
          Refresh
        </BButton>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Business</th>
              <th>Status</th>
              <th>AI Surveys Created</th>
              <th>Credits Used</th>
              <th>AI Cost</th>
              <th>Total AI Points</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {businesses.length > 0 ? (
              businesses.map((business) => (
                <tr key={business.business_id}>
                  <td>{business.business_name}</td>
                  <td>{business.is_active ? 'Active' : 'Deactivated'}</td>
                  <td>{business.ai_surveys_created ?? business.services_created ?? 0}</td>
                  <td>{business.credits_used || 0}</td>
                  <td>{formatCurrency(business.total_ai_cost_usd)}</td>
                  <td>{business.total_ai_points || 0}</td>
                  <td>
                    <button
                      className={styles.actionButton}
                      onClick={() => navigate(`/admin/business/manage/${business.business_id}/ai-usage`)}
                    >
                      Manage Access & Points
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className={styles.emptyCell}>
                  No AI analytics data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
};

export default BusinessAIAnalyticsOverview;
